import type { WalletDetailItemType } from './WalletDetails.types';
import type { ReactElement } from 'react';

import { useStackModal } from '@rango-dev/internal-profile/hooks';
import { Box, Flex } from '@rango-dev/whale';
import { Modal } from '@rango-dev/whale/lab';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso as VirtualizedList } from 'react-virtuoso';

import { useAppSelector } from '../../hooks/reduxHooks';
import { useWalletDetailsItem } from '../../hooks/useWalletDetailsItem';

import { EmptyState } from './EmptyState';
import { compareEmptyWalletDetailItem, getUsdValue, MIN_USD_VALUE } from './WalletDetails.helpers';
import styles from './WalletDetails.module.css';
import { WalletDetailsFilter } from './WalletDetailsFilter';
import { WalletDetailsHeader } from './WalletDetailsHeader/WalletDetailsHeader';
import { WalletDetailsItem } from './WalletDetailsItem/WalletDetailsItem';
import { WalletDetailsProfile } from './WalletDetailsProfile';

export function WalletDetails(): ReactElement {
  const { walletDetailItems } = useWalletDetailsItem();
  const { pop } = useStackModal();
  const { t } = useTranslation();
  const {
    hideEmptyWallets,
    hideSmallBalances,
    hideUnsupportedTokens,
    excludedBlockchains,
    excludedWallets,
  } = useAppSelector((state) => state.walletDetails);

  const secondaryLoading = walletDetailItems.some(
    (item) => !item?.account?.loading || item?.account?.balances?.length,
  );

  const applyHideEmptyWalletFilter = (walletDetailItem: WalletDetailItemType) => {
    const { balances } = walletDetailItem.account;
    return !secondaryLoading || !hideEmptyWallets || balances?.length;
  };

  const applyHideSmallBalancesFilter = (walletDetailItem: WalletDetailItemType) => {
    const { balances } = walletDetailItem.account;
    return (
      !secondaryLoading ||
      !hideEmptyWallets ||
      !hideSmallBalances ||
      balances?.some((balanceItem) => getUsdValue(balanceItem)?.gte(MIN_USD_VALUE))
    );
  };

  const applyHideUnsupportedTokensFilter = (walletDetailItem: WalletDetailItemType) => {
    const { balances } = walletDetailItem.account;
    return (
      !secondaryLoading ||
      !hideEmptyWallets ||
      !hideUnsupportedTokens ||
      balances?.some((balanceItem) => balanceItem?.isSupported)
    );
  };

  const applyExcludedBlockchainsFilter = (walletDetailItem: WalletDetailItemType) => {
    const { name: blockChainName } = walletDetailItem;
    return !excludedBlockchains.includes(blockChainName);
  };

  const applyExcludedWalletsFilter = (walletDetailItem: WalletDetailItemType) => {
    const { walletType } = walletDetailItem;
    return walletType.some((walletTypeItem) => !excludedWallets.includes(walletTypeItem));
  };

  const applyMainFilters = (walletDetailItem: WalletDetailItemType) => {
    return (
      applyExcludedBlockchainsFilter(walletDetailItem) &&
      applyExcludedWalletsFilter(walletDetailItem)
    );
  };

  const applyEmptyWalletFilters = (walletDetailItem: WalletDetailItemType) => {
    return (
      applyHideEmptyWalletFilter(walletDetailItem) &&
      applyHideSmallBalancesFilter(walletDetailItem) &&
      applyHideUnsupportedTokensFilter(walletDetailItem)
    );
  };

  // Apply only the main filters including Wallet and Blockchain
  const mainFilteredItems = walletDetailItems.filter(applyMainFilters);

  // Apply the rest of the filters including empty wallets, small balances and unsupported tokens
  const finalFilteredItems = mainFilteredItems
    .filter(applyEmptyWalletFilters)
    .sort(compareEmptyWalletDetailItem);

  const emptyWalletsCount = mainFilteredItems.length - finalFilteredItems.length;

  // Determine if the empty state should be shown
  const showEmptyState = !(finalFilteredItems && finalFilteredItems.length > 0);
  return (
    <Modal.Panel title={t('Your Wallet')} onClose={pop} idPrefix="wallet-details-modal">
      <Flex direction="column" gap="5" height="100%">
        <WalletDetailsProfile />
        <WalletDetailsHeader />
        <Flex direction="column" gap="4" p="4" flexGrow="1" minHeight="0" className={styles.panel}>
          <WalletDetailsFilter />
          <Box position="relative" flexGrow="1" minHeight="0">
            {showEmptyState ? (
              <EmptyState emptyWalletsCount={emptyWalletsCount} />
            ) : (
              <div className={styles.list}>
                <VirtualizedList
                  totalCount={finalFilteredItems.length}
                  itemContent={(index) => {
                    const walletDetailItem = finalFilteredItems[index];
                    return (
                      <Box
                        key={`${walletDetailItem.name}-${walletDetailItem.account.address}`}
                        pb="2">
                        <WalletDetailsItem walletDetailItem={walletDetailItem} />
                      </Box>
                    );
                  }}
                />
                <div className={styles.fader} />
              </div>
            )}
          </Box>
        </Flex>
      </Flex>
    </Modal.Panel>
  );
}
